import { CTAListModuleType } from "@/modules/CTAList/CTAList.client";
import { WPClient } from "@/wp/client/WPClient";
import { CTAListElementType } from "@/modules/CTAList/CTAListBox/CTAListBox";
// import { Modul_Linkliste } from "@aleksundshantu/gp-types/acf-module";

type CTAListElements = Exclude<
  CTAListModuleType["cta_list"]["elements"],
  false
>;

interface FetchLinkListItemsProps {
  page: number;
  postsPerPage: number;
  offset: number;
  locale: string | undefined;
  defaultLocale: string | undefined;
  module: CTAListModuleType;
  labels: {
    glossarBtn: string;
    postsBtn: string;
    quotesBtn: string;
    teamBtn: string;
    eventsBtn: string;
    magazineBtn: string;
  };
}

const DEFAULT_LINK_EXTENDED: CTAListElements[number]["link_extended"] = {
  type: "link",
  link: null,
  download: {
    link_target: false,
    link_text: "",
    link_aria_label: "",
  },
  email: {
    link_target: "",
    link_text: "",
    link_aria_label: "",
  },
  telephone: {
    link_target: "",
    link_text: "",
    link_aria_label: "",
  },
  pop_up: {
    link_text: "",
    link_aria_label: "",
  },
};

export const fetchCTAListItems = async ({
  module,
  page,
  postsPerPage,
  offset,
  locale,
  defaultLocale,
  labels,
}: FetchLinkListItemsProps): Promise<{
  posts: CTAListElementType[];
  totalPosts: number;
}> => {
  const {
    type,
    posts: selectedPosts,
    events,
    teams,
    quotes,
    magazine,
    glossar,
    elements,
  } = module.cta_list;
  if (type === "manual") {
    const staticElements = (elements || []) as CTAListElementType[];
    return { posts: staticElements, totalPosts: staticElements.length };
  }

  const client = new WPClient({ locale, defaultLocale });

  /* jscpd:ignore-start */
  if (type === "magazine") {
    const hasSpecificIds = Array.isArray(magazine) && magazine.length > 0;

    const result = await client.getPosts({
      type: "magazine",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_magazine.title",
        // "acf.header_magazine.overline",
        "acf.header_magazine.image",
      ],
      ...(hasSpecificIds
        ? { includeIds: { ids: magazine, keepOrder: true } }
        : {}),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_magazine;
      return {
        svg: header?.image || false,
        tagline: "",
        title: header?.title || "",
        link: {
          title: labels.magazineBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies CTAListElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "blog") {
    const hasSpecificIds =
      Array.isArray(selectedPosts) && selectedPosts.length > 0;

    const result = await client.getPosts({
      type: "blog",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_post.title",
        "acf.header_post.media.image_desktop",
        "date",
      ],
      ...(hasSpecificIds && {
        includeIds: { ids: selectedPosts, keepOrder: true },
      }),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_post;
      return {
        svg: header.media.image_desktop,
        tagline: new Date(post.date).toLocaleDateString(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
        title: header?.title || "",
        link: {
          title: labels.postsBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies CTAListElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "quotes") {
    const hasSpecificIds = Array.isArray(quotes) && quotes.length > 0;

    const result = await client.getPosts({
      type: "quotes",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_quotes.title",
        "acf.header_quotes.name",
        "acf.header_quotes.image",
      ],
      ...(hasSpecificIds && { includeIds: { ids: quotes, keepOrder: true } }),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_quotes;
      return {
        svg: header.image,
        tagline: header?.name || "",
        title: header?.title || "",
        link: {
          title: labels.quotesBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies CTAListElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "team") {
    const hasSpecificIds = Array.isArray(teams) && teams.length > 0;

    const result = await client.getPosts({
      type: "team",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_team.image",
        "acf.header_team.firstname",
        "acf.header_team.name",
        "acf.header_team.position",
        // "acf.header_team.content",
      ],
      ...(hasSpecificIds && { includeIds: { ids: teams, keepOrder: true } }),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_team;
      return {
        svg: header?.image || false,
        tagline: header?.position || "",
        title: `${header?.firstname || ""} ${header?.name || ""}`.trim(),
        // content: header?.content || "",
        link: {
          title: labels.teamBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies CTAListElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "events") {
    const hasSpecificIds = Array.isArray(events) && events.length > 0;

    const result = await client.getPosts({
      type: "events",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_events.title",
        "acf.header_events.media.image_desktop",
        "acf.header_events.date",
      ],
      ...(hasSpecificIds && { includeIds: { ids: events, keepOrder: true } }),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_events;
      return {
        svg: header?.media?.image_desktop || false,
        tagline: header?.date
          ? new Date(header.date).toLocaleDateString(locale, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "",
        title: header?.title || "",
        link: {
          title: labels.eventsBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies CTAListElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "glossary") {
    const hasSpecificIds = Array.isArray(glossar) && glossar.length > 0;

    const result = await client.getPosts({
      type: "glossary",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_glossary.title",
        "acf.header_glossary.media.image_desktop",
      ],
      ...(hasSpecificIds && { includeIds: { ids: glossar, keepOrder: true } }),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_glossary;
      return {
        svg: header.media.image_desktop,
        tagline: "",
        title: header?.title || "",
        link: {
          title: labels.glossarBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies CTAListElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  /* jscpd:ignore-end */

  return { posts: [], totalPosts: 0 };
};
